import { Form, Validators } from '@happybandit/react-validation';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { Alert, AlertTitle } from '@material-ui/lab';
import { IPackageMoveBody } from '@shared/interfaces/lib/contractInterfaces';
import { MetrcFacilitySubCollections } from '@shared/interfaces/lib/firebaseConstants';
import { IPackage, RoomTypes } from '@shared/interfaces/lib/interfaces';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { postMovePackage, ResponseError } from '../../../api';
import { selectUser, setShowPlantDetails } from '../../../redux/appSlice';
import { PlantDetailPages } from '../../../redux/interfaces';
import { useFacilityRooms } from '../../../utils';
import SelectValid from '../../SelectValid';

interface IProps {
    packageItem: IPackage;
    facilityId: string;
}

const useStyles = makeStyles( ( theme: Theme ) =>
    createStyles( {
        appBar: {
            position: 'relative',
        },
        title: {
            marginLeft: theme.spacing( 2 ),
            flex: 1,
        },
        level: {
            textTransform: 'capitalize',
        },
        content: {
            padding: theme.spacing( 2 ),
            minHeight: 100,
            backgroundColor: theme.palette.grey[200],
        },
        breadcrumb: {
            marginBottom: theme.spacing( 2 ),
        }
    } ),
);

const MovePackage: React.FC<IProps> = ( { packageItem, facilityId } ) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const rooms = useFacilityRooms( facilityId );
    const user = useSelector( selectUser );
    const [locationId, setLocationId] = useState( '' );
    const [loading, setLoading] = useState( false );
    const [errors, setErrors] = useState<string[]>( [] );

    const packageRooms = rooms.filter( ( room ) => (room.types || []).includes( RoomTypes.packaging ) );

    const handleSubmit = ( valid: boolean ) => {
        if ( !valid || !user ) {
            return;
        }
        setLoading( true );
        setErrors( [] );

        const body: IPackageMoveBody = {
            facilityId,
            locationId: parseInt(locationId),
            date: DateTime.local().toISODate(),
            packageLabels: [packageItem.metrc.Label],
        }
        postMovePackage( user.token, body )
            .then( () => {
                dispatch( setShowPlantDetails( {
                    key: {
                        collection: MetrcFacilitySubCollections.metrcPackages,
                        id: packageItem.id,
                    },
                    page: PlantDetailPages.details,
                } ) );
            } )
            .catch( async ( e: ResponseError ) => {
                if ( e.response.status === 400 ) {
                    const body: { errors: string[] } = await e.response.json();
                    setErrors( body.errors );
                }
            } )
            .finally( () => {
                setLoading( false );
            } );
    };

    return (
        <>
            <AppBar className={classes.appBar}>
                <Toolbar>
                    <IconButton
                        edge="start" color="inherit" onClick={() => dispatch( setShowPlantDetails( undefined ) )}
                        aria-label="close"
                    >
                        <CloseIcon/>
                    </IconButton>
                    <Typography variant="h6" className={classes.title}>
                        Change Package Location
                    </Typography>
                </Toolbar>
            </AppBar>
            <div className={classes.content}>
                {loading ? (
                    <CircularProgress size={24}/>
                ) : (
                    <>
                        {errors.length > 0 && (
                            <Alert variant="filled" severity="error">
                                <AlertTitle>Change Failed</AlertTitle>
                                {errors.map( ( error ) => (
                                    <div>{error}</div>
                                ) )}
                            </Alert>
                        )}
                        <Typography variant="h6">
                            Current Location: {packageItem.metrc.LocationName}
                        </Typography>
                        <Form onSubmit={handleSubmit}>
                            <SelectValid
                                id="package-location"
                                value={locationId}
                                label="Storage Room to move to"
                                fullWidth
                                validators={[
                                    Validators.required(locationId),
                                ]}
                                onChange={setLocationId}
                            >
                                <MenuItem value={''}/>
                                {packageRooms.map( ( item ) => (
                                    <MenuItem key={item.id} value={item.id}>{item.metrc.Name}</MenuItem>
                                ) )}
                            </SelectValid>
                            <hr style={{ 'borderTop': '#eee' }}/>
                            <Button
                                variant="contained"
                                type="submit"
                            >
                                Submit Change
                            </Button>
                        </Form>
                    </>
                )}
            </div>
        </>
    )
};

export default MovePackage;